import { useLayoutEffect } from 'react';
import {
  type AnimationPlaybackControls,
  mix,
  type useMotionValue,
  useMotionValueEvent,
  useTransform
} from 'framer-motion';

/**
 * Similar to framer-motions mix function, but input range
 * is a string.
 *
 * **Usage:**
 * ```ts
 * const mixer = mixString('level')
 * console.log(mixer(0)) // ''
 * console.log(mixer(0.5)) // 'lev'
 * console.log(mixer(1)) // 'level'
 * ```
 *
 * @param {String} string input string
 * @returns {(progress: number) => string} function that takes a progress range [0, 1] and outputs a substring.
 */
export function mixString(string: string) {
  const _mixer = mix(0, string.length);

  return function mixer(progress: number) {
    const interpolated = _mixer(progress);
    const index = Math.floor(interpolated + 0.5);
    return string.substring(0, index);
  };
}

function flattenChildTextNodes(node: Node): Node[] {
  if (node.nodeType === Node.TEXT_NODE) return [node];

  if (node.hasChildNodes())
    return [...node.childNodes].flatMap(n => flattenChildTextNodes(n));

  return [];
}

/**
 * Behaves similar to GSAPs split text utility where given an html
 * node it will wrap each letter in the element in a `<span class="char">{letter}</span>`
 * allowing for stagger animations on characters.
 *
 * @param {HTMLElement} el html element to split chars on
 */
export function splitChars(
  el: HTMLElement,
  opts: {
    class?: string;
  } = {}
) {
  const childNodes = flattenChildTextNodes(el);
  childNodes.forEach(node => {
    if (!node.nodeValue) return node;
    const charEls = node.nodeValue.split('').map(char => {
      const span = document.createElement('span');
      span.classList.add(opts.class || 'char');
      const content = document.createTextNode(char);
      span.appendChild(content);
      return span;
    });

    const parentEl = node.parentElement;
    if (!parentEl) return;

    charEls.forEach(charEl => parentEl.insertBefore(charEl, node));
    parentEl.removeChild(node);
  });
}

export function useSyncAnimationToMotionValue(
  controller: AnimationPlaybackControls | null,
  motionValue: ReturnType<typeof useMotionValue<number>>,
  opts: {
    range?: [number, number];
  } = {}
) {
  const progress = useTransform(motionValue, opts.range || [0, 1], [0, 1]);

  useLayoutEffect(() => {
    if (controller === null) return;

    controller.pause();
    controller.time = 0;
  }, [controller]);

  useMotionValueEvent(progress, 'change', v => {
    if (controller === null) return;

    controller.time = v * controller.duration;
  });
}
